import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { TPass } from "src/shared/types/globalStotre.types";
import { usePassErrorMessageModal } from "./PassErrorMessageModal";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useSeatGeek } from "src/components/reusable/hooks/useSeatGeek";
import { useTicketsdotcom } from "src/components/reusable/hooks/useTicketsdotcom";
import { useTicketmaster } from "src/components/reusable/hooks/useTicketmaster";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { toast } from "react-toastify";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { globalLanguages } from "src/shared/global_languages";
import { usePageTopNotification } from "src/components/reusable/components/PageTopNotification";
import { checkoutTabs } from "./usePurchaseFlow";
import { useVendorAuth } from "src/components/reusable/hooks/useVendorAuth";

export function usePassFlow() {
  const router = useRouterExt();
  const pageTopNotification = usePageTopNotification();
  const { VENDOR, LANGUAGES, TEAM_GLOBAL_SETTINGS } = useTeamConfig();
  const { cart, passes, setGlobalState, session } = useGlobalStore();
  const urlCode = router.query.urlCode as string;
  const passErrorMessageModal = usePassErrorMessageModal();
  const { connectSeatGeek } = useSeatGeek();
  const { connectTicketsdotcom } = useTicketsdotcom();
  const { connectTicketmaster } = useTicketmaster();
  const { loginWithVendor } = useVendorAuth();
  const { getPassesApi, getPassesOnAccountApi, getBundlesApi } = useApiRoutes();
  const inviteeUserId = router.query.inviteeUserId;

  const handleGetPasses = async () => {
    try {
      const params: Record<string, any> = {};
      if (inviteeUserId) {
        params.queryParams = { inviteeUserId: String(inviteeUserId) };
      }
      const passes = await getPassesApi.get(params);
      setGlobalState({ passes });
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const handleGetPassesUrlCode = async (urlCode: string) => {
    try {
      const passes = await getPassesApi.get({
        queryParams: { urlCode },
      });
      setGlobalState({ passes });
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const handleGetPassesOnAccount = async () => {
    try {
      const passesOnAccount = await getPassesOnAccountApi.get();
      setGlobalState({ passesOnAccount });
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const onClickSubscribe = async (passId: number) => {
    const passDetails = passes.find((p) => p.passId == passId) as TPass;
    if (passDetails?.errorMessage) {
      return passErrorMessageModal.set({
        isOpen: true,
        errorMessage: passDetails.errorMessage,
      });
    }

    if (session) {
      router.pushSchema({
        pathname: routes.checkout,
        query: { ...router.query, tab: "checkout", passId: String(passId) },
      });
      return;
    } else {
      const state = {
        authOrigin: constants.authOrigins.purchasePlan,
        redirectRoute: {
          pathname: routes.checkout,
          query: {
            tab: constants.checkoutTabs.checkout,
            passId: String(passId),
            ...(urlCode && { urlCode }),
          },
        },
      };

      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return await connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return await connectTicketsdotcom(state);
      }

      connectTicketmaster({ state });
    }
  };

  const onClickSendAsGift = async (passId: number) => {
    const isGiftToggleDisabled = TEAM_GLOBAL_SETTINGS?.DISABLE_STANDARD_PURCHASE_ON_GIFT_PASSES; // prettier-ignore

    const query: Record<string, string> = {
      tab: "checkout",
      passId: String(passId),
      isGift: "1",
    };

    if (isGiftToggleDisabled) query.isGiftToggleDisabled = "1";

    if (session) {
      router.pushSchema({
        pathname: routes.checkout,
        query: { ...router.query, ...query },
      });
      return;
    } else {
      return await loginWithVendor({
        authOrigin: constants.authOrigins.purchasePlan,
        redirectRoute: {
          pathname: routes.checkout,
          query,
          ...(urlCode && { urlCode }),
        },
      });
    }
  };

  const onChangeCartPassSeatCount = (pass: TPass) => {
    let cartPasses = [...(cart?.passes || [])];
    const isPassInCart = cartPasses?.some((p) => p.passId === pass.passId);

    // should not be able to decrease seat count below min seat count
    if (isPassInCart && pass.seatCount < pass.minSeatCount) {
      cartPasses = cartPasses.map((itm) => {
        if (itm.passId == pass.passId) itm.seatCount = pass.minSeatCount;
        return itm;
      });
      setGlobalState({ cart: { ...cart, passes: cartPasses } });
      return;
    }

    if (isPassInCart && pass.seatCount > 0) {
      cartPasses = cartPasses.map((itm) => {
        if (itm.passId == pass.passId) itm.seatCount = pass.seatCount;
        return itm;
      });
    } else if (isPassInCart && pass.seatCount === 0) {
      cartPasses = cartPasses.filter((itm) => itm.passId !== pass.passId);
    } else if (!isPassInCart && pass.seatCount > 0) {
      cartPasses.push(pass);
    }

    setGlobalState({ cart: { ...cart, passes: cartPasses } });
  };

  const setPassPurchaseSuccessMessage = () => {
    const Title =
      LANGUAGES?.PASS_PURCHASE_SUCCESS_MESSAGE?.TITLE ||
      globalLanguages.PURCHASE_SUCCESS_MESSAGE.TITLE;
    const Description =
      LANGUAGES?.PASS_PURCHASE_SUCCESS_MESSAGE?.DESCRIPTION ||
      globalLanguages.PURCHASE_SUCCESS_MESSAGE.DESCRIPTION;

    pageTopNotification.set({
      isOpen: true,
      route: routes.home,
      title: <Title />,
      description: <Description />,
    });
  };

  const onClickPassGroupPurchase = async () => {
    if (!session) {
      const state = {
        authOrigin: constants.authOrigins.passGroupCard,
        redirectRoute: {
          pathname: routes.checkout,
          query: { tab: checkoutTabs.checkout },
        },
      };
      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return await connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return await connectTicketsdotcom(state);
      }

      return await connectTicketmaster({ state });
    }
    await router.pushSchema({
      pathname: routes.checkout,
      query: { tab: checkoutTabs.checkout },
    });
  };

  async function handleGetBundles(bundleId?: number) {
    try {
      const passBundles = await getBundlesApi.get({ appendPath: bundleId });
      setGlobalState({ passBundles });
      if (bundleId) {
        const passes = passBundles.find((bundle) => bundle.id === bundleId)?.passes; // prettier-ignore
        if (passes) setGlobalState({ passes });
      }
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  }

  async function handleClickBundle(bundleId: number) {
    await router.pushSchema({
      pathname: routes.offerId,
      query: { ...router.query, offerId: String(bundleId) },
    });
  }

  return {
    handleGetPasses,
    handleGetPassesUrlCode,
    handleGetPassesOnAccount,
    onChangeCartPassSeatCount,
    onClickSubscribe,
    setPassPurchaseSuccessMessage,
    onClickPassGroupPurchase,
    handleGetBundles,
    handleClickBundle,
    onClickSendAsGift,
  };
}
